import React, { useCallback, useMemo, useRef } from 'react';
import { Close, DeleteForever, AutoAwesome } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './Generator.css';
import '../styles/responsive.css';
import BuildStats from '../components/stats/BuildStats';
import Generate from '../components/generator/Generate';
import GeneratorSkeleton from '../components/generator/GeneratorSkeleton';
import BuildPanel from '../components/build/BuildPanel';
import { useResponsive } from '../hooks/useResponsive';
import { usePointerHover } from '../hooks/usePointerHover';
import { useSlotTooltips } from '../hooks/useSlotTooltips';
import { useNotification } from '../components/NotificationSystem';
import SpellsPanel from '../components/spells/SpellsPanel';
import BoostsPanel from '../components/boosts/BoostsPanel';
import { useGeneratorContext } from '../contexts/GeneratorStateContext';
import BuildSetsPanel from '../components/build/BuildSetsPanel';
import BuildBackground from '../components/build/BuildBackground';
import DropdownMenu from '../components/utils/DropdownMenu';
import Tooltip from '../components/utils/Tooltip';
import ScrollIcon from '../components/utils/ScrollIcon';
import WeightIcon from '../components/utils/WeightIcon';
import PositionToggle from '../components/generator/PositionToggle';
import FmMenu from '../components/generator/FmMenu';
import Tabs from '../components/utils/Tabs';
import GeneratorHeader from '../components/generator/GeneratorHeader';
import DataHeaderSection from '../components/generator/DataHeaderSection';
import CriteriaList from '../components/generator/CriteriaList';
import { SheetCriteriaSummary, SheetCriteriaList } from '../components/generator/MobileSheetCriteria';
import DefenseShareControl from '../components/generator/DefenseShareControl';
import BuildInfoPanel from '../components/generator/BuildInfoPanel';
import GenerationSummaryPanel from '../components/generator/GenerationSummaryPanel';
import GeneratorTooltips from '../components/generator/GeneratorTooltips';
import FlowLayoutToggle, { useFlowLayout } from '../components/generator/FlowLayoutToggle';
import PageTemplate from '../components/layout/PageTemplate';
import BuildScore, { BuildScoreSheetRow } from '../components/build/BuildScore';
import LazyMount from '../components/utils/LazyMount';
import { scoreToColor } from '../utils/colorUtils';
import { DISPLAY_MODE_GENERATOR, DISPLAY_MODE_SIMPLE, normalizeDefenseShare } from '../core/GenConfig';
import { WEIGHT_MAX, WEIGHT_MIN } from '../core/criteriaWeights';
import { useGenerationJob } from '../hooks/useGenerationJob';
import { useDataTabRouting } from '../hooks/useDataTabRouting';
import { useBuildPanelControls } from '../hooks/useBuildPanelControls';
import { useCriteriaAndScore } from '../hooks/useCriteriaAndScore';
import { useBuildIdentityActions } from '../hooks/useBuildIdentityActions';
import { useGenerationRun } from '../hooks/useGenerationRun';

/** When true, stats/spells/weapon column is on the left; build/generate on the right. */
const SWAP_GRID_COLUMNS = false;
/** When true, show the bottom "Infos" panel (notes / placeholders). */
const SHOW_INFO_PANEL = false;
const GENERATION_BUSY_STATUSES = new Set(['queued', 'booting', 'initializing', 'loading', 'finalizing']);
const isGenerationBusyStatus = (status) => GENERATION_BUSY_STATUSES.has(status);

// Text constants
const TXT_BUILD_NOT_FOUND = { fr: 'Stuff introuvable', en: 'Build not found', de: 'Build nicht gefunden', es: 'Build no encontrada', pt: 'Build não encontrada' };
const TXT_BUILD_NOT_FOUND_DESC = { fr: "Ce stuff n'existe pas ou a été supprimé.", en: 'This build does not exist or has been deleted.', de: 'Dieser Build existiert nicht oder wurde gelöscht.', es: 'Esta build no existe o ha sido eliminada.', pt: 'Esta build não existe ou foi excluída.' };

// Section titles
const TXT_TITLE_STATS = { fr: 'stats', en: 'stats', de: 'Stats', es: 'stats', pt: 'stats' };
const TXT_TITLE_SPELLS = { fr: 'sorts', en: 'spells', de: 'Zauber', es: 'hechizos', pt: 'feitiços' };
const TXT_TITLE_BOOSTS = { fr: 'boosts', en: 'boosts', de: 'Boosts', es: 'boosts', pt: 'boosts' };
const TXT_TITLE_BUILD = { fr: 'stuff', en: 'build', de: 'Build', es: 'build', pt: 'build' };
const TXT_MENU = { fr: 'Réinitialiser les sélections', en: 'Reset selections', de: 'Auswahl zurücksetzen', es: 'Restablecer las selecciones', pt: 'Redefinir as seleções' };
const TXT_BUILD_GRID_MENU = { fr: 'Vider le stuff', en: 'Empty build', de: 'Build leeren', es: 'Vaciar la build', pt: 'Esvaziar a build' };
const TXT_BUILD_ACTION_CLEAR = { fr: 'Vider', en: 'Empty', de: 'Leeren', es: 'Vaciar', pt: 'Esvaziar' };
const TXT_BUILD_ACTION_RESET = { fr: 'Déverrouiller et vider', en: 'Reset build', de: 'Entsperren und leeren', es: 'Desbloquear y vaciar', pt: 'Destravar e esvaziar' };
const TXT_TAB_STATS = { fr: 'stats', en: 'stats', de: 'Stats', es: 'stats', pt: 'stats' };
const TXT_TAB_SPELLS = { fr: 'sorts', en: 'spells', de: 'Zauber', es: 'hechizos', pt: 'feitiços' };
const TXT_TAB_BOOSTS = { fr: 'boosts', en: 'boosts', de: 'Boosts', es: 'boosts', pt: 'boosts' };
const TXT_BAR_GENERATOR_LABEL = { fr: 'générateur', en: 'generator', de: 'Generator', es: 'generador', pt: 'gerador' };
const TXT_BAR_GENERATOR_TOGGLE_TIP = { fr: 'Activer/Désactiver le générateur', en: 'Toggle the generator', de: 'Generator ein-/ausschalten', es: 'Activar/Desactivar el generador', pt: 'Ativar/Desativar o gerador' };
const TXT_CRITERIA_ACTION_RESET_STATS = { fr: 'Réinitialiser les critères de stats', en: 'Reset stats criteria', de: 'Stat-Kriterien zurücksetzen', es: 'Restablecer los criterios de stats', pt: 'Redefinir os critérios de stats' };
const TXT_CRITERIA_ACTION_RESET_DAMAGE = { fr: 'Réinitialiser les critères de sorts', en: 'Reset spells criteria', de: 'Zauber-Kriterien zurücksetzen', es: 'Restablecer los criterios de hechizos', pt: 'Redefinir os critérios de feitiços' };
const TXT_CRITERIA_ACTION_RESET_BOOSTS = { fr: 'Réinitialiser les boosts', en: 'Reset boosts', de: 'Boosts zurücksetzen', es: 'Restablecer los boosts', pt: 'Redefinir os boosts' };
const TXT_CRITERIA_ACTION_RESET_WEIGHTS = { fr: 'Réinitialiser les poids', en: 'Reset weights', de: 'Gewichtungen zurücksetzen', es: 'Restablecer los pesos', pt: 'Redefinir os pesos' };
const TXT_SCROLLS_ON = { fr: 'Parchemins activés (+100)', en: 'Scrolls on (+100)', de: 'Schriftrollen aktiviert (+100)', es: 'Pergaminos activados (+100)', pt: 'Pergaminhos ativados (+100)' };
const TXT_SCROLLS_OFF = { fr: 'Parchemins désactivés', en: 'Scrolls off', de: 'Schriftrollen deaktiviert', es: 'Pergaminos desactivados', pt: 'Pergaminhos desativados' };
const TXT_WEIGHTS = { fr: 'Afficher les poids', en: 'Display weights', de: 'Gewichtungen anzeigen', es: 'Mostrar los pesos', pt: 'Exibir os pesos' };
// {min}/{max} are filled from the clamp in core/criteriaWeights.js, so the range
// quoted here cannot drift from the range the field actually enforces.
const TXT_WEIGHTS_HINT = {
    fr: "Permet de donner plus ou moins d'importance à certains critères (entre ×{min} et ×{max}).",
    en: 'Gives more or less importance to certain criteria (between ×{min} and ×{max}).',
    de: 'Gewichtet einzelne Kriterien stärker oder schwächer (zwischen ×{min} und ×{max}).',
    es: 'Permite dar más o menos importancia a ciertos criterios (entre ×{min} y ×{max}).',
    pt: 'Permite dar mais ou menos importância a certos critérios (entre ×{min} e ×{max}).',
};

const Generator = ({ lang }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { dataTab, userSlug, buildCode } = useParams();
    const { isMobile, isSmall, isMedium, isWide } = useResponsive();
    const hasPointerHover = usePointerHover();
    const { showNotification } = useNotification();

    // Use state hook for state management
    const {
        build,
        buildName,
        genConfig,
        activeBuildId,
        isReadOnly,
        isBootstrapping,
        currentBuildMeta,
        updateCurrentBuildMeta,
        setBuild,
        setBuildName,
        setGenConfig,
        deleteBuild,
        createBuild,
        duplicateBuild,
        openBuild,
        exportState,
        getActiveBuildPath,
        routeBootstrap,
        openCanonicalBuild,
        isAuthenticated,
    } = useGeneratorContext();

    // Data tab (stats/spells) selection + URL sync — see hooks/useDataTabRouting.js
    const { activeDataTab, handleDataTabChange } = useDataTabRouting({
        dataTab, navigate, getActiveBuildPath, lang,
    });
    const savedDisplayMode = genConfig?.displayMode === DISPLAY_MODE_SIMPLE
        ? DISPLAY_MODE_SIMPLE
        : DISPLAY_MODE_GENERATOR;
    const isPublicReadOnly = currentBuildMeta?.scope === 'remote' && !currentBuildMeta?.canEdit;
    const effectiveDisplayMode = isPublicReadOnly
        ? DISPLAY_MODE_SIMPLE
        : (isMobile ? DISPLAY_MODE_GENERATOR : savedDisplayMode);
    const canEditBuild = !isReadOnly;
    const canEditGenerator = canEditBuild && effectiveDisplayMode === DISPLAY_MODE_GENERATOR;
    const showGeneratorControls = effectiveDisplayMode === DISPLAY_MODE_GENERATOR;
    const showCompactDesktopData = !showGeneratorControls && !isMobile;
    // Dev experiment (FlowLayoutToggle): no panels, sticky build, stacked sections.
    const [flowLayoutPref, setFlowLayoutPref] = useFlowLayout();
    const flowLayout = flowLayoutPref && !isMobile;
    // Stats/spells/boosts as stacked sections with their own titles, not tabs.
    const stackedData = isMobile || showCompactDesktopData || flowLayout;
    const generationJob = useGenerationJob();
    const generationStatus = generationJob.status;
    // Browser-side runs spend the whole budget searching, so the progress gauge
    // uses different bands from the server's pre/search/tail split.
    const generationIsLocal = generationJob.isLocalRun;
    const isGenerationActive = generationJob.isGenerating;

    const handleBuildNoteChange = useCallback((text) => {
        if (!canEditBuild) return;
        const next = typeof text === 'string' ? text : '';
        setGenConfig((prev) => prev.update('buildNote', next));
    }, [canEditBuild, setGenConfig]);

    const statsTabLabel = TXT_TAB_STATS[lang] || TXT_TAB_STATS.en;
    const spellsTabLabel = TXT_TAB_SPELLS[lang] || TXT_TAB_SPELLS.en;

    // Criteria sheet, targets & score (spell scoring data prefetch/fetch,
    // criteria-tag sync, active-criteria list w/ per-item handlers, build
    // score) — see hooks/useCriteriaAndScore.js
    // Note: the hook also returns `spellScoreTerms` (not destructured here —
    // consumed by cluster A's generate() via the hook's return contract).
    const {
        spellTypeId,
        spellScoringData,
        criteriaTagIds,
        setCriteriaTagIds,
        activeTargetsCount,
        spellTabCriteriaCount,
        statWeightsCount,
        spellWeightsCount,
        dataTabCriteriaTooltips,
        activeCriteria,
        spellLineIndicatorMap,
        buildScoreResult,
        headerScoreColor,
        handleToggleSetting,
        handleResetStatsCriteria,
        handleResetDamageCriteria,
        handleResetStatWeights,
        handleResetSpellWeights,
    } = useCriteriaAndScore({
        build,
        setBuild,
        canEditBuild,
        canEditGenerator,
        isGenerationActive,
        genConfig,
        setGenConfig,
        lang,
        statsTabLabel,
        spellsTabLabel,
    });

    // Build panel wiring (level control, empty/reset build, invalid-build gating
    // text, character-render capture) — see hooks/useBuildPanelControls.js
    const {
        lvl,
        lvlStat,
        invalidBuildStatus,
        blockingInvalidBuildReasonText,
        handleResetBuildSlots,
        handleResetBuildAndLocks,
        handleCharacterRenderCaptured,
        handleCharacterViewChange,
        handleLevelChange,
    } = useBuildPanelControls({
        build,
        canEditBuild,
        setBuild,
        genConfig,
        setGenConfig,
        lang,
        spellScoringData,
        spellTypeId,
    });

    // Build identity: routing/canonical-URL bootstrap, create/duplicate/
    // import/delete/share/rename actions, build-name input focus — see
    // hooks/useBuildIdentityActions.js. Composes with useGeneratorContext()
    // (context pieces passed in as params below) rather than duplicating it.
    const {
        buildNameInputRef,
        shareBusy,
        routeNotFound,
        isCopyDisabled,
        handleHeaderLikeToggle,
        handleCopyBuild,
        handleCreateBuild,
        handleDisplayModeChange,
        handleImportBuild,
        handleShareBuild,
        handleExportBuild,
        handleDeleteBuild,
    } = useBuildIdentityActions({
        navigate,
        location,
        lang,
        userSlug,
        buildCode,
        build,
        buildName,
        genConfig,
        setGenConfig,
        activeBuildId,
        isReadOnly,
        isPublicReadOnly,
        canEditBuild,
        isGenerationActive,
        activeDataTab,
        savedDisplayMode,
        currentBuildMeta,
        updateCurrentBuildMeta,
        isAuthenticated,
        deleteBuild,
        createBuild,
        duplicateBuild,
        openBuild,
        exportState,
        getActiveBuildPath,
        routeBootstrap,
        openCanonicalBuild,
        showNotification,
    });

    const showBuildInfoPanel = useMemo(() => (
        !!(showGeneratorControls
            || currentBuildMeta?.scope === 'remote'
            || criteriaTagIds.length > 0)
    ), [showGeneratorControls, currentBuildMeta?.scope, criteriaTagIds.length]);

    const dataTabsElement = useMemo(() => (
        <Tabs
            items={[
                {
                    value: 'stats',
                    label: TXT_TAB_STATS[lang] || TXT_TAB_STATS.en,
                    badge: showGeneratorControls ? activeTargetsCount : undefined,
                    tooltip: showGeneratorControls ? dataTabCriteriaTooltips.stats : undefined,
                },
                {
                    value: 'spells',
                    label: TXT_TAB_SPELLS[lang] || TXT_TAB_SPELLS.en,
                    badge: showGeneratorControls ? spellTabCriteriaCount : undefined,
                    tooltip: showGeneratorControls ? dataTabCriteriaTooltips.spells : undefined,
                },
                {
                    // No badge: a boost is not a criterion — the count beside
                    // the other two tabs means "things the generator will chase",
                    // and a number here would claim the same of a self-buff.
                    value: 'boosts',
                    label: TXT_TAB_BOOSTS[lang] || TXT_TAB_BOOSTS.en,
                },
            ]}
            value={activeDataTab}
            onChange={handleDataTabChange}
            ariaLabel="Data section"
            className="data-tab"
        />
    ), [lang, activeDataTab, activeTargetsCount, spellTabCriteriaCount, dataTabCriteriaTooltips, handleDataTabChange, showGeneratorControls]);

    // Tooltip state (item + set tooltips) — see hooks/useSlotTooltips.js
    const {
        tooltip,
        tooltipPos,
        tooltipElRef,
        slotTooltipWeaponMaging,
        setTooltipData,
        setTooltipPosition,
        handleTooltipShow,
        handleTooltipHide,
        handleTooltipLeave,
        handleSetTooltipShow,
        handleSetTooltipHide,
    } = useSlotTooltips({ build, hasPointerHover });

    const handleDefenseShareChange = useCallback((nextShare) => {
        if (!canEditGenerator || isGenerationActive) return;
        setGenConfig(prev => prev.update('defenseShare', normalizeDefenseShare(nextShare)));
    }, [canEditGenerator, isGenerationActive, setGenConfig]);
    const defenseShareControlProps = {
        lang,
        defenseShare: genConfig?.defenseShare,
        onDefenseShareChange: handleDefenseShareChange,
        disabled: !canEditGenerator || isGenerationActive,
    };
    // Shared with cluster D/F (still inline below): D/F reset it to `false` in
    // the `setBuild` wrappers passed to `BuildPanel`/`DataHeaderSection`; F
    // reads `.current` as `isServerUpdate`; the generation-run hook below
    // writes it to `true` while applying a streamed/solved build. One
    // instance only — never give any of these a local copy.
    const isServerUpdateRef = useRef(false);

    // Weights toggle + scroll (parchotage) toggle + criteria reset + position
    // toggle: one row of elements, rendered in the desktop tabs row and in the
    // mobile/compact stats header alike, in that order. The position toggle
    // shows on every tab; the other two only where they mean something.
    const handlePositionChange = useCallback((next) => {
        if (!canEditBuild || isGenerationActive) return;
        setGenConfig(prev => prev.update('position', next));
    }, [canEditBuild, isGenerationActive, setGenConfig]);

    const positionToggleElement = (!build || !canEditBuild || isPublicReadOnly) ? null : (
        <PositionToggle
            value={genConfig?.position}
            onChange={handlePositionChange}
            lang={lang}
            disabled={isGenerationActive}
        />
    );

    const handleTranscendenceChange = useCallback((on) => {
        if (!build || !canEditBuild || isGenerationActive) return;
        isServerUpdateRef.current = false; // User-initiated change
        setBuild(build.setTranscendence(on));
    }, [build, canEditBuild, isGenerationActive, setBuild]);

    const handleFmExoChange = useCallback((statId, on) => {
        if (!build || !canEditBuild || isGenerationActive) return;
        isServerUpdateRef.current = false; // User-initiated change
        setBuild(build.setFmExo(statId, on));
    }, [build, canEditBuild, isGenerationActive, setBuild]);

    const fmMenuProps = (!build || !canEditBuild || isPublicReadOnly) ? null : {
        exos: build.fmExos,
        onExoChange: handleFmExoChange,
        transcendence: build.isTranscendenceOn(),
        onTranscendenceChange: handleTranscendenceChange,
        lang,
        disabled: isGenerationActive,
    };

    const isFullyScrolled = build ? build.isFullyScrolled() : true;
    const canToggleScrolls = !!build && canEditBuild && !isGenerationActive;

    const handleToggleScrolls = useCallback(() => {
        if (!build || !canEditBuild || isGenerationActive) return;
        isServerUpdateRef.current = false; // User-initiated change
        setBuild(build.setAllScrolls(!build.isFullyScrolled()));
    }, [build, canEditBuild, isGenerationActive, setBuild]);

    const renderScrollToggle = (tab) => {
        if (!build || !canEditBuild || isPublicReadOnly) return null;
        // Scrolls are characteristics: the stats tab is where they show.
        if (tab !== 'stats') return null;
        const label = (isFullyScrolled ? TXT_SCROLLS_ON : TXT_SCROLLS_OFF)[lang]
            || (isFullyScrolled ? TXT_SCROLLS_ON : TXT_SCROLLS_OFF).en;
        return (
            <Tooltip content={label}>
                <button
                    type="button"
                    className={`scroll-toggle${isFullyScrolled ? ' is-active' : ''}`}
                    aria-label={label}
                    aria-pressed={isFullyScrolled}
                    disabled={!canToggleScrolls}
                    onClick={handleToggleScrolls}
                >
                    <ScrollIcon className="scroll-toggle-glyph" />
                </button>
            </Tooltip>
        );
    };

    // Weights column toggle (stats + spells panels). Persisted on the build's
    // genConfig, so reopening a build reopens it the way it was left.
    const showWeights = genConfig?.displayWeights === true;
    // Takes an explicit value from the sheet's checkbox; the desktop button calls
    // it with a click event, which is not a boolean, so that path still flips.
    const handleToggleWeights = useCallback((next) => {
        if (!canEditGenerator) return;
        setGenConfig(prev => prev.update(
            'displayWeights',
            typeof next === 'boolean' ? next : prev.displayWeights !== true,
        ));
    }, [canEditGenerator, setGenConfig]);

    const renderWeightsToggle = (tab) => {
        if (!showGeneratorControls || !canEditGenerator) return null;
        // Mobile has no weights column to toggle: the panels are too narrow for
        // one, so weights are edited per row in the gen bar's criteria sheet,
        // which has a row per criterion and room to spare.
        if (isMobile) return null;
        // A boost is not a criterion, so the boosts tab has no weights to show.
        if (tab === 'boosts') return null;
        const label = TXT_WEIGHTS[lang] || TXT_WEIGHTS.en;
        const hint = (TXT_WEIGHTS_HINT[lang] || TXT_WEIGHTS_HINT.en)
            .replace('{min}', String(WEIGHT_MIN))
            .replace('{max}', String(WEIGHT_MAX));
        const tip = (
            <span className="weights-toggle-tip">
                <span className="weights-toggle-tip__title">{label}</span>
                <span className="weights-toggle-tip__hint">{hint}</span>
            </span>
        );
        return (
            <Tooltip content={tip}>
                <button
                    type="button"
                    className={`weights-toggle${showWeights ? ' is-active' : ''}`}
                    aria-label={label}
                    aria-pressed={showWeights}
                    onClick={handleToggleWeights}
                >
                    <WeightIcon className="weights-toggle-glyph" />
                </button>
            </Tooltip>
        );
    };

    const pickedBoostsCount = build ? Object.keys(build.getBoosts()).length : 0;
    const handleResetBoosts = useCallback(() => {
        if (!build || !canEditBuild || isGenerationActive) return;
        isServerUpdateRef.current = false; // User-initiated change
        setBuild(build.clearBoosts());
    }, [build, canEditBuild, isGenerationActive, setBuild]);

    // Only what the given tab shows, and only what has something to reset:
    // a tab with nothing to reset gets no menu at all.
    const renderCriteriaReset = (tab) => {
        if (!showGeneratorControls || !canEditGenerator) return null;
        const tabItems = {
            stats: [
                activeTargetsCount > 0 && {
                    label: TXT_CRITERIA_ACTION_RESET_STATS[lang] || TXT_CRITERIA_ACTION_RESET_STATS.en,
                    onClick: handleResetStatsCriteria,
                },
                statWeightsCount > 0 && {
                    label: TXT_CRITERIA_ACTION_RESET_WEIGHTS[lang] || TXT_CRITERIA_ACTION_RESET_WEIGHTS.en,
                    onClick: handleResetStatWeights,
                },
            ],
            spells: [
                spellTabCriteriaCount > 0 && {
                    label: TXT_CRITERIA_ACTION_RESET_DAMAGE[lang] || TXT_CRITERIA_ACTION_RESET_DAMAGE.en,
                    onClick: handleResetDamageCriteria,
                },
                spellWeightsCount > 0 && {
                    label: TXT_CRITERIA_ACTION_RESET_WEIGHTS[lang] || TXT_CRITERIA_ACTION_RESET_WEIGHTS.en,
                    onClick: handleResetSpellWeights,
                },
            ],
            boosts: [
                pickedBoostsCount > 0 && {
                    label: TXT_CRITERIA_ACTION_RESET_BOOSTS[lang] || TXT_CRITERIA_ACTION_RESET_BOOSTS.en,
                    onClick: handleResetBoosts,
                    disabled: isGenerationActive,
                },
            ],
        };
        const items = (tabItems[tab] || []).filter(Boolean);
        if (items.length === 0) return null;
        return (
            <DropdownMenu
                items={items}
                dropdownClassName="criteria-summary-menu"
                menuClassName="criteria-summary-menu-list"
                showButtonLabel={false}
                triggerIcon="reset"
                buttonTooltip={TXT_MENU[lang] || TXT_MENU.en}
                buttonContent={<span className="criteria-summary-menu-trigger" />}
            />
        );
    };

    // The same actions, boxed for a section header. Mobile/compact put the
    // active tab's set in the stats header; the flow layout gives each section
    // its own, with the position toggle on stats only.
    const renderDataActions = (tab, withPosition = true) => {
        const weights = renderWeightsToggle(tab);
        const scroll = renderScrollToggle(tab);
        const reset = renderCriteriaReset(tab);
        const position = withPosition ? positionToggleElement : null;
        if (!weights && !scroll && !reset && !position) return null;
        return (
            <div className="generator-data-actions row">
                {weights}
                {scroll}
                {reset}
                {position}
            </div>
        );
    };

    // Generation run control (SSE switchboard, live score/summary state,
    // stop/cleanup machinery) — see hooks/useGenerationRun.js. `generationJob`
    // itself stays a direct `useGenerationJob()` call above (not inside this
    // hook): B (useCriteriaAndScore) and E (useBuildIdentityActions) both
    // need its `isGenerationActive` and are called earlier, so their outputs
    // (`setCriteriaTagIds`, ...) can be threaded into this hook as params
    // instead of the other way around.
    const {
        canGenerate,
        generate,
        handleStop,
        liveGenSoftScore,
        liveGenMaximizeScore,
        topCandidates,
        topSetCandidates,
        genSummaryOpen,
        setGenSummaryOpen,
        genSummaryExpanded,
        setGenSummaryExpanded,
        liveMetrics,
        finalMetrics,
        scoreTimeSeries,
        elitePool,
        workerMetrics,
        aggregateMetrics,
        isGenSummaryLive,
    } = useGenerationRun({
        build,
        setBuild,
        genConfig,
        lang,
        showNotification,
        canEditGenerator,
        isGenerationActive,
        generationJob,
        blockingInvalidBuildReasonText,
        lvl,
        setCriteriaTagIds,
        isServerUpdateRef,
    });

    const swapCols = (cols) => (SWAP_GRID_COLUMNS ? cols.split(' ').reverse().join(' ') : cols);
    const swapAreas = (areas) => (SWAP_GRID_COLUMNS
        ? areas.replace(/"([^"]+)"/g, (_, s) => {
            const names = s.trim().split(/\s+/);
            return names.length >= 2 ? `"${names.reverse().join(' ')}"` : `"${s}"`;
        })
        : areas);

    const GRID_TPL_SMALL = swapCols('min-content 1fr');
    const GRID_TPL_MEDIUM = swapCols('min-content 1fr');
    const GRID_TPL_WIDE = swapCols('min-content 1fr');

    // Base areas; swapCols/swapAreas flip column order when SWAP_GRID_COLUMNS is true.
    const GRID_AREAS_TWO_COL = swapAreas(`
                "build data"
            `);
    const GRID_AREAS_TWO_COL_SIMPLE = GRID_AREAS_TWO_COL;

    // Mobile: character/level live in the build grid center.
    const GRID_AREAS_MOBILE = `
                "build"
                "data"
            `;
    const GRID_AREAS_MOBILE_SIMPLE = GRID_AREAS_MOBILE;
    const GRID_AREAS_MOBILE_NO_BUILD = `
                "build"
                "data"
            `;

    const GRID_AREAS_SMALL = GRID_AREAS_TWO_COL;
    const GRID_AREAS_DESKTOP = GRID_AREAS_TWO_COL;

    const layoutConfig = {
        mobile: {
            gridTemplateColumns: '1fr',
            gridTemplateAreas: showGeneratorControls ? GRID_AREAS_MOBILE : GRID_AREAS_MOBILE_SIMPLE,
        },
        small: {
            gridTemplateColumns: GRID_TPL_SMALL,
            gridTemplateAreas: showGeneratorControls ? GRID_AREAS_SMALL : GRID_AREAS_TWO_COL_SIMPLE,
        },
        medium: {
            gridTemplateColumns: GRID_TPL_MEDIUM,
            gridTemplateAreas: showGeneratorControls ? GRID_AREAS_DESKTOP : GRID_AREAS_TWO_COL_SIMPLE,
        },
        wide: {
            gridTemplateColumns: GRID_TPL_WIDE,
            gridTemplateAreas: showGeneratorControls ? GRID_AREAS_DESKTOP : GRID_AREAS_TWO_COL_SIMPLE,
        }
    };

    // Helper function to get current layout configuration
    const getCurrentLayout = () => {
        if (isMobile) {
            return {
                ...layoutConfig.mobile,
                gridTemplateAreas: build ? GRID_AREAS_MOBILE : GRID_AREAS_MOBILE_NO_BUILD
            };
        }
        if (isSmall) return layoutConfig.small;
        if (isMedium) return layoutConfig.medium;
        if (isWide) return layoutConfig.wide;
        return layoutConfig.medium; // fallback
    };

    // Helper function to render elements based on their keys
    const renderElement = (elementKey) => {
        const hasScore = buildScoreResult != null;
        const renderBuildSetsPanel = (showTitle = false) => (
            <BuildSetsPanel
                build={build}
                setBuild={setBuild}
                lang={lang}
                {...(hasPointerHover ? {
                    onTooltipShow: handleTooltipShow,
                    onTooltipHide: handleTooltipHide,
                    onSetTooltipShow: handleSetTooltipShow,
                    onSetTooltipHide: handleSetTooltipHide,
                } : {})}
                isGenerating={isGenerationBusyStatus(generationStatus)}
                isServerUpdate={isServerUpdateRef.current}
                showTitle={showTitle}
                canEditBuild={canEditBuild}
            />
        );

        const renderDataTabsRow = () => (
            <div className="generator-data-tabs-row row">
                <div className="generator-data-tabs-main row">
                    {dataTabsElement}
                    {renderWeightsToggle(activeDataTab)}
                    {renderScrollToggle(activeDataTab)}
                    {renderCriteriaReset(activeDataTab)}
                    {positionToggleElement}
                </div>
            </div>
        );

        const renderCriteriaList = () => (
            !showGeneratorControls ? null : (
                <CriteriaList
                    lang={lang}
                    activeCriteria={activeCriteria}
                    score={buildScoreResult?.score}
                    scoreColor={headerScoreColor}
                    breakdown={buildScoreResult}
                    spellLineIndicatorMap={spellLineIndicatorMap}
                    disabled={!canEditGenerator || isGenerationActive}
                    showScore={showGeneratorControls}
                />
            )
        );

        // A stacked section's title, with that section's buttons beside it.
        const renderSectionHeader = (title, actions) => (
            <div className="data-section-header row">
                <h3 className="left">{title[lang] || title.en}</h3>
                {actions}
            </div>
        );

        const elements = {
            criteriaList: !showGeneratorControls ? null : (
                <div key="criteria-list-cell" className="generator-criteria-list-cell">
                    {renderCriteriaList()}
                </div>
            ),
            headerCell: null,
            dataTabsBarCell: stackedData ? null : (
                <div key="data-tabs-bar-cell" className="generator-data-tabs-criteria-mobile">
                    {renderDataTabsRow()}
                </div>
            ),
            generateBar: null,
            main: (
                <div key="main" className="main-section row " style={{ gridArea: 'main' }}>
                    <div key="build" className={`build-section column`} >
                        <h4 className='left'>{TXT_TITLE_BUILD[lang] || TXT_TITLE_BUILD.en}</h4>
                        <div className="build-section-content column">
                            <BuildBackground
                                build={build}
                                onCharacterRenderCaptured={handleCharacterRenderCaptured}
                                onCharacterViewChange={handleCharacterViewChange}
                            />
                            <BuildPanel
                                build={build}
                                setBuild={(newBuild) => {
                                    isServerUpdateRef.current = false; // User-initiated change
                                    setBuild(newBuild);
                                }}
                                {...(hasPointerHover ? { onTooltipShow: handleTooltipShow, onTooltipHide: handleTooltipHide } : {})}
                                genConfig={genConfig}
                                setGenConfig={setGenConfig}
                                isGenerating={isGenerationBusyStatus(generationStatus)}
                                isServerUpdate={isServerUpdateRef.current}
                                lang={lang}
                                canEditBuild={canEditBuild}
                                canEditGenerator={canEditGenerator}
                                lvl={lvl}
                                lvlStat={lvlStat}
                                onLevelChange={handleLevelChange}
                            />
                        </div>
                    </div>
                    <div key="stats" className='stats-section column'>
                        <h4 className='left'>{TXT_TITLE_STATS[lang] || TXT_TITLE_STATS.en}</h4>
                        <BuildStats
                            build={build}
                            setBuild={setBuild}
                            lang={lang}
                            genConfig={genConfig}
                            setGenConfig={setGenConfig}
                            isGenerating={isGenerationActive}
                            mobileTargetCheckbox={isMobile}
                            canEditBuild={canEditBuild}
                            canEditGenerator={canEditGenerator}
                        />
                    </div>
                </div>
            ),
            statsSection: (
                <div key="stats-section" className="stats-section column" style={{ gridArea: 'stats-section' }}>
                    {stackedData
                        ? renderSectionHeader(TXT_TITLE_STATS, renderDataActions(flowLayout ? 'stats' : activeDataTab))
                        : null}
                    <BuildStats
                        build={build}
                        setBuild={setBuild}
                        lang={lang}
                        genConfig={genConfig}
                        setGenConfig={setGenConfig}
                        isGenerating={isGenerationActive}
                        mobileTargetCheckbox={isMobile}
                        canEditBuild={canEditBuild}
                        canEditGenerator={canEditGenerator}
                    />
                </div>
            ),
            spellsSection: (
                <div key="spells-section" className="spells-section column" style={{ gridArea: 'spells-section' }}>
                    {stackedData
                        ? renderSectionHeader(TXT_TITLE_SPELLS, flowLayout ? renderDataActions('spells', false) : null)
                        : null}
                    <LazyMount enabled={stackedData}>
                        <SpellsPanel
                            build={build}
                            setBuild={setBuild}
                            lvl={lvl}
                            lang={lang}
                            isGenerating={isGenerationActive}
                            genConfig={genConfig}
                            setGenConfig={setGenConfig}
                            mobileTargetCheckbox={isMobile}
                            canEditGenerator={canEditGenerator}
                            showNote={showGeneratorControls}
                        />
                    </LazyMount>
                </div>
            ),
            boostsSection: (
                <div key="boosts-section" className="boosts-section column" style={{ gridArea: 'boosts-section' }}>
                    {stackedData
                        ? renderSectionHeader(TXT_TITLE_BOOSTS, flowLayout ? renderDataActions('boosts', false) : null)
                        : null}
                    <LazyMount enabled={stackedData}>
                        <BoostsPanel
                            build={build}
                            setBuild={setBuild}
                            lvl={lvl}
                            lang={lang}
                            isGenerating={isGenerationActive}
                            canEditBuild={canEditBuild}
                            genConfig={genConfig}
                            setGenConfig={setGenConfig}
                            showNote={showGeneratorControls}
                        />
                    </LazyMount>
                </div>
            ),
            buildSection: (
                <div key="build-section" className={`build-section column${flowLayout ? '' : ' panel'}`} style={{ gridArea: 'build' }}>
                    <BuildBackground
                        build={build}
                        onCharacterRenderCaptured={handleCharacterRenderCaptured}
                        onCharacterViewChange={handleCharacterViewChange}
                        className={flowLayout ? 'build-background--flow' : ''}
                    />
                    {flowLayout ? null : <div className="build-section-data-header">
                        <DataHeaderSection
                            className="build-panel-data-section"
                            lang={lang}
                            build={build}
                            setBuild={(newBuild) => {
                                isServerUpdateRef.current = false;
                                setBuild(newBuild);
                            }}
                            lvl={lvl}
                            lvlStat={lvlStat}
                            onLevelChange={handleLevelChange}
                            isGenerationActive={isGenerationActive || isReadOnly}
                            canEditBuild={canEditBuild}
                            invalidBuildStatus={invalidBuildStatus}
                        />
                    </div>}
                    <BuildPanel
                        build={build}
                        setBuild={(newBuild) => {
                            isServerUpdateRef.current = false;
                            setBuild(newBuild);
                        }}
                        {...(hasPointerHover ? { onTooltipShow: handleTooltipShow, onTooltipHide: handleTooltipHide } : {})}
                        genConfig={genConfig}
                        setGenConfig={setGenConfig}
                        isGenerating={isGenerationBusyStatus(generationStatus)}
                        isServerUpdate={isServerUpdateRef.current}
                        lang={lang}
                        canEditBuild={canEditBuild}
                        canEditGenerator={canEditGenerator}
                        lvl={lvl}
                        lvlStat={lvlStat}
                        onLevelChange={handleLevelChange}
                        buildTopBadge={canEditBuild ? (
                            <div className="build-grid-menu-wrapper">
                                <DropdownMenu
                                    items={[
                                        {
                                            icon: <Close fontSize="small" />,
                                            label: TXT_BUILD_ACTION_CLEAR[lang] || TXT_BUILD_ACTION_CLEAR.en,
                                            onClick: handleResetBuildSlots,
                                        },
                                        {
                                            icon: <DeleteForever fontSize="small" />,
                                            label: TXT_BUILD_ACTION_RESET[lang] || TXT_BUILD_ACTION_RESET.en,
                                            onClick: handleResetBuildAndLocks,
                                        },
                                    ]}
                                    dropdownClassName="build-grid-menu"
                                    menuClassName="build-grid-menu-list"
                                    showButtonLabel={false}
                                    triggerIcon="close"
                                    buttonTooltip={TXT_BUILD_GRID_MENU[lang] || TXT_BUILD_GRID_MENU.en}
                                    buttonContent={<span className="build-grid-menu-trigger" />}
                                />
                            </div>
                        ) : null}
                    />
                    {renderBuildSetsPanel(false)}
                </div>
            ),
            sets: (
                <div key="sets" className="sets-section column" style={{ gridArea: 'sets' }}>
                    {/* <h4 >SETS</h4> */}
                    <BuildSetsPanel
                        build={build}
                        setBuild={setBuild}
                        lang={lang}
                        {...(hasPointerHover ? {
                            onTooltipShow: handleTooltipShow,
                            onTooltipHide: handleTooltipHide,
                            onSetTooltipShow: handleSetTooltipShow,
                            onSetTooltipHide: handleSetTooltipHide,
                        } : {})}
                        isGenerating={isGenerationBusyStatus(generationStatus)}
                        isServerUpdate={isServerUpdateRef.current}
                        canEditBuild={canEditBuild}
                    />
                </div>
            ),
            generate: (
                !showGeneratorControls ? null :
                    <div key="generate" className="generate-section row" style={{ gridArea: 'generate' }}>
                        <Generate
                            className="gen-button"
                            onGenerate={generate}
                            onStop={handleStop}
                            genConfig={genConfig}
                            setGenConfig={setGenConfig}
                            onToggleSetting={handleToggleSetting}
                            status={generationStatus}
                            localSolve={generationIsLocal}
                            disabled={!canGenerate}
                            lang={lang}
                            build={build}
                            invalidBuildReason={blockingInvalidBuildReasonText}
                            timeLimit={genConfig.timeLimit}
                            scoreValue={buildScoreResult?.score}
                            scoreColor={hasScore ? scoreToColor(buildScoreResult.score) : undefined}
                            liveSoftScore={liveGenSoftScore}
                            liveMaximizeScore={liveGenMaximizeScore}
                        />
                    </div>
            ),
            divider: (
                <div key="divider" className="generator-divider" style={{ gridArea: 'divider' }}>
                </div>
            ),
        };
        return elements[elementKey];
    };

    const renderFixedGeneratorBar = () => {
        if (!canEditBuild && !showGeneratorControls) return null;

        const generatorBarLabel = TXT_BAR_GENERATOR_LABEL[lang] || TXT_BAR_GENERATOR_LABEL.en;
        const generatorBarToggleTip = TXT_BAR_GENERATOR_TOGGLE_TIP[lang] || TXT_BAR_GENERATOR_TOGGLE_TIP.en;

        const displayToggle = !isMobile && canEditBuild && !isPublicReadOnly ? (
            <Tooltip content={generatorBarToggleTip}>
                <button
                    type="button"
                    className={`generator-bar-mode-toggle row${effectiveDisplayMode === DISPLAY_MODE_GENERATOR ? ' is-active' : ''}${isGenerationActive ? ' is-disabled' : ''}`}
                    aria-label={generatorBarToggleTip}
                    disabled={isGenerationActive}
                    onClick={() => {
                        handleDisplayModeChange(
                            effectiveDisplayMode === DISPLAY_MODE_GENERATOR
                                ? DISPLAY_MODE_SIMPLE
                                : DISPLAY_MODE_GENERATOR
                        );
                    }}
                >
                    <AutoAwesome fontSize="small" />
                </button>
            </Tooltip>
        ) : null;

        const displayTitle = isMobile && showGeneratorControls ? (
            <div className="generator-bar-mode-title row" aria-hidden="true">
                <AutoAwesome fontSize="small" />
                <h6>{generatorBarLabel}</h6>
            </div>
        ) : null;

        return (
            <Generate
                onGenerate={generate}
                onStop={handleStop}
                genConfig={genConfig}
                setGenConfig={setGenConfig}
                status={generationStatus}
                            localSolve={generationIsLocal}
                disabled={!canGenerate}
                lang={lang}
                build={build}
                invalidBuildReason={blockingInvalidBuildReasonText}
                scoreValue={buildScoreResult?.score}
                scoreColor={headerScoreColor}
                liveSoftScore={liveGenSoftScore}
                liveMaximizeScore={liveGenMaximizeScore}
                layout="generatorBar"
                showGeneratorControls={showGeneratorControls}
                displayMode={effectiveDisplayMode}
                displayModeToggle={displayToggle}
                displayModeTitle={displayTitle}
                defenseControls={(
                    <DefenseShareControl {...defenseShareControlProps} />
                )}
                fmControl={fmMenuProps && <FmMenu {...fmMenuProps} />}
                sheetFmContent={fmMenuProps && <FmMenu {...fmMenuProps} variant="sheet" />}
                criteriaContent={(
                    <CriteriaList
                        lang={lang}
                        activeCriteria={activeCriteria}
                        spellLineIndicatorMap={spellLineIndicatorMap}
                        disabled={!canEditGenerator || isGenerationActive}
                        showScore={false}
                        className="criteria-list--bottom"
                    />
                )}
                scoreContent={(
                    <BuildScore
                        score={buildScoreResult?.score}
                        lang={lang}
                        color={headerScoreColor}
                        breakdown={buildScoreResult}
                        spellLineIndicatorMap={spellLineIndicatorMap}
                        compact
                        bordered
                    />
                )}
                sheetSummaryContent={(
                    <SheetCriteriaSummary
                        activeCriteria={activeCriteria}
                        lang={lang}
                    />
                )}
                sheetDefenseContent={(
                    <DefenseShareControl {...defenseShareControlProps} variant="sheet" />
                )}
                sheetScoreContent={(
                    <BuildScoreSheetRow
                        score={buildScoreResult?.score}
                        lang={lang}
                        breakdown={buildScoreResult}
                        spellLineIndicatorMap={spellLineIndicatorMap}
                    />
                )}
                sheetCriteriaContent={(
                    <SheetCriteriaList
                        activeCriteria={activeCriteria}
                        lang={lang}
                        spellLineIndicatorMap={spellLineIndicatorMap}
                        disabled={!canEditGenerator || isGenerationActive}
                        showWeights={showWeights}
                        onToggleWeights={canEditGenerator ? handleToggleWeights : null}
                    />
                )}
            />
        );
    };

    // Helper function to render grid layout
    const renderGrid = () => {
        const layout = getCurrentLayout();

        return (
            <div className="generator-page-wrap">
                <div className="generator-layout column">
                    <div
                        className="generator-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: layout.gridTemplateColumns,
                            gridTemplateAreas: layout.gridTemplateAreas,
                        }}
                    >
                        {isMobile ? (
                            <>
                                {renderElement('buildSection')}
                                <div className="generator-data-stack column" style={{ gridArea: 'data' }}>
                                    <div className="data-column column">
                                        {renderElement('statsSection')}
                                        <hr className="section-divider" />
                                        {renderElement('spellsSection')}
                                        <hr className="section-divider" />
                                        {renderElement('boostsSection')}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {renderElement('buildSection')}
                                <div
                                    className={`data-column column${flowLayout ? '' : ' panel'}${showCompactDesktopData ? ' data-column--compact-desktop' : ''}`}
                                    style={{ gridArea: 'data' }}
                                >
                                    {renderElement('dataTabsBarCell')}
                                    {flowLayout ? (
                                        <>
                                            {renderGeneratorHeader()}
                                            <hr className="section-divider" />
                                            <DataHeaderSection
                                                lang={lang}
                                                build={build}
                                                setBuild={(newBuild) => {
                                                    isServerUpdateRef.current = false;
                                                    setBuild(newBuild);
                                                }}
                                                lvl={lvl}
                                                lvlStat={lvlStat}
                                                onLevelChange={handleLevelChange}
                                                isGenerationActive={isGenerationActive || isReadOnly}
                                                canEditBuild={canEditBuild}
                                                invalidBuildStatus={invalidBuildStatus}
                                            />
                                            <hr className="section-divider" />
                                        </>
                                    ) : null}
                                    {stackedData ? (
                                        <>
                                            {renderElement('statsSection')}
                                            <hr className="section-divider" />
                                            {renderElement('spellsSection')}
                                            <hr className="section-divider" />
                                            {renderElement('boostsSection')}
                                        </>
                                    ) : (
                                        <>
                                            {activeDataTab === 'stats' && renderElement('statsSection')}
                                            {activeDataTab === 'spells' && renderElement('spellsSection')}
                                            {activeDataTab === 'boosts' && renderElement('boostsSection')}
                                        </>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    {renderFixedGeneratorBar()}
                    {SHOW_INFO_PANEL && (
                        <div className="info-section panel column">
                            <h4>Infos</h4>
                            <div className="info-placeholder"></div>
                            <div className="info-placeholder"></div>
                            <div className="info-placeholder"></div>
                            <p>Note:</p>
                            <textarea
                                className="build-note-input"
                                value=""
                                onChange={() => { }}
                                placeholder="..."
                            />
                        </div>
                    )}
                    {showBuildInfoPanel && (
                        <BuildInfoPanel
                            className="build-info-panel--stacked"
                            createdAt={currentBuildMeta?.createdAt}
                            updatedAt={currentBuildMeta?.updatedAt}
                            criteriaTags={criteriaTagIds}
                            lang={lang}
                            buildNote={genConfig?.buildNote ?? ''}
                            onBuildNoteChange={handleBuildNoteChange}
                            noteResetKey={activeBuildId || ''}
                            readOnly={!canEditBuild}
                        />
                    )}
                    {genSummaryOpen && (topCandidates || topSetCandidates || liveMetrics || finalMetrics) && (
                        <GenerationSummaryPanel
                            lang={lang}
                            onClose={() => setGenSummaryOpen(false)}
                            candidates={topCandidates}
                            setCandidates={topSetCandidates}
                            liveMetrics={liveMetrics}
                            finalMetrics={finalMetrics}
                            scoreTimeSeries={scoreTimeSeries}
                            elitePool={elitePool}
                            workerMetrics={workerMetrics}
                            aggregateMetrics={aggregateMetrics}
                            isLive={isGenSummaryLive}
                            open={genSummaryExpanded}
                            onToggle={() => setGenSummaryExpanded((v) => !v)}
                        />
                    )}
                    {isMobile ? null : <FlowLayoutToggle value={flowLayoutPref} onChange={setFlowLayoutPref} />}
                </div>
            </div>
        );
    };

    // Flow layout renders it at the top of the right column instead of above the grid.
    const renderGeneratorHeader = () => (routeNotFound || isBootstrapping ? null : (
            <GeneratorHeader
                lang={lang}
                buildName={buildName}
                setBuildName={setBuildName}
                buildNameInputRef={buildNameInputRef}
                isGenerationActive={isGenerationActive}
                isReadOnly={isReadOnly}
                onCreateBuild={handleCreateBuild}
                onCopyBuild={handleCopyBuild}
                onShareBuild={handleShareBuild}
                onExportBuild={handleExportBuild}
                onDeleteBuild={handleDeleteBuild}
                onImportBuild={handleImportBuild}
                isPublicReadOnly={isPublicReadOnly}
                displayMode={effectiveDisplayMode}
                onDisplayModeChange={handleDisplayModeChange}
                showModeSelector={false}
                isCopyDisabled={isCopyDisabled}
                isShareDisabled={isCopyDisabled}
                isShareBusy={shareBusy}
                isExportDisabled={isGenerationActive || !build}
                isDeleteDisabled={isGenerationActive || isReadOnly || !activeBuildId}
                headerStartContent={null}
                owner={currentBuildMeta?.owner}
                metrics={showBuildInfoPanel ? currentBuildMeta?.metrics : null}
                viewerHasLiked={!!currentBuildMeta?.viewer?.hasLiked}
                onToggleLike={handleHeaderLikeToggle}
                isLikeDisabled={!activeBuildId || currentBuildMeta?.scope !== 'remote'}
            />
    ));

    const handleWrapperClick = useCallback((e) => {
        if (!isGenerationActive) return;

        const target = e.target;
        const isStopButton =
            target.closest('.gen-button .primary') || target.closest('.gen-button-stop-overlay');
        const isGeneratorDataTab = target.closest('.data-tab');
        const isGenSummaryHeader = target.closest('.gen-summary-header');

        if (!isStopButton && !isGeneratorDataTab && !isGenSummaryHeader) {
            e.preventDefault();
            e.stopPropagation();
        }
    }, [isGenerationActive]);

    return (
        <PageTemplate
            header={false}
        >
            {flowLayout ? null : renderGeneratorHeader()}
            <div
                className={`generator-wrapper column${flowLayout ? ' generator-wrapper--flow' : ''}${isGenerationActive ? ' generation-active' : ''}${!showGeneratorControls ? ' generator-wrapper--simple' : ''}${isPublicReadOnly ? ' generator-wrapper--readonly' : ''}`}
                onClick={handleWrapperClick}
                onClickCapture={handleWrapperClick}
            >
                {/* <SimpleElementBackground build={build} /> */}
                {/* <div className="background-overlay" /> */}
                {/* <ElementBackground build={build} /> */}
                {/* <LinearElementBackground build={build} /> */}
                {/* <div className="overlay"></div> */}
                {/* <AnimBackground /> */}


                {isBootstrapping ? <GeneratorSkeleton /> : (routeNotFound ? (
                    <div className="generator-not-found column">
                        <h2>{TXT_BUILD_NOT_FOUND[lang] || TXT_BUILD_NOT_FOUND.en}</h2>
                        <p>{TXT_BUILD_NOT_FOUND_DESC[lang] || TXT_BUILD_NOT_FOUND_DESC.en}</p>
                    </div>
                ) : renderGrid())}

                {/* Item/set tooltips rendered at Generator level */}
                <GeneratorTooltips
                    tooltip={tooltip}
                    tooltipPos={tooltipPos}
                    tooltipElRef={tooltipElRef}
                    onTooltipLeave={handleTooltipLeave}
                    onTooltipDismiss={handleTooltipHide}
                    build={build}
                    lang={lang}
                    weaponMaging={slotTooltipWeaponMaging}
                    setTooltipData={setTooltipData}
                    setTooltipPosition={setTooltipPosition}
                />
            </div>
        </PageTemplate>
    );
};

export default Generator;

